import { isBlockingAppointmentStatus } from "../../utils/chairScheduling";
import {
  appointmentBelongsToDateDoc,
  appointmentRecordKeyForSlotMerge,
  formatSlotRangeLabelFromMinutes,
  getAppointmentDisplayRangeMinutes,
  parseClockLabelToMinutes,
} from "../../utils/appointmentTimeDisplay";
import { STANDARD_OFFICE_SLOT_LABELS } from "../../utils/dentistSchedulePlots";

export function getHalfHourSlotsCoveringRange(
  startMinutes,
  endMinutes,
  slotLabels = STANDARD_OFFICE_SLOT_LABELS
) {
  if (!Array.isArray(slotLabels)) return [];
  return slotLabels.filter((label) => {
    const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
    const slotStart = parseClockLabelToMinutes(startRaw);
    const slotEnd = parseClockLabelToMinutes(endRaw);
    if (
      !Number.isFinite(slotStart) ||
      !Number.isFinite(slotEnd) ||
      slotEnd <= slotStart
    ) {
      return false;
    }
    return slotStart < endMinutes && slotEnd > startMinutes;
  });
}

export function getBlockingAppointmentsForDentistOnDate(
  allAppointments,
  dateKey,
  dentistUid
) {
  if (!dateKey || !dentistUid) return [];
  return (Array.isArray(allAppointments) ? allAppointments : []).filter((appt) => {
    if (!appt || !isBlockingAppointmentStatus(appt.status)) return false;
    if (!appointmentBelongsToDateDoc(appt, dateKey)) return false;
    return resolveAppointmentDentistUid(appt) === dentistUid;
  });
}

/** Build merged Manage Calendar modal rows from plotted slots plus any booked coverage. */
export function buildManageCalendarRenderableSlots({
  plottedSlotLabels = [],
  appointments = [],
  dateKey,
  dentistUid,
}) {
  if (!dateKey || !dentistUid) return [];

  const labelSet = new Set(
    Array.isArray(plottedSlotLabels) ? plottedSlotLabels : []
  );

  getBlockingAppointmentsForDentistOnDate(appointments, dateKey, dentistUid).forEach(
    (appt) => {
      const range = getAppointmentDisplayRangeMinutes(appt);
      if (!range) return;
      getHalfHourSlotsCoveringRange(range.start, range.end).forEach((slot) => {
        labelSet.add(slot);
      });
    }
  );

  const slotLabels = STANDARD_OFFICE_SLOT_LABELS.filter((label) => labelSet.has(label));
  if (slotLabels.length === 0) return [];

  const appointmentSlotMap = buildAppointmentSlotMapForDates(
    appointments,
    [dateKey],
    dentistUid,
    slotLabels
  );

  return buildMergedScheduleSlotMenuOptions(slotLabels, appointmentSlotMap).map(
    (option) => {
      const [startRaw, endRaw] = option.displayLabel
        .split(" - ")
        .map((part) => part.trim());
      const start = parseClockLabelToMinutes(startRaw);
      const end = parseClockLabelToMinutes(endRaw);
      return {
        start: Number.isFinite(start) ? start : undefined,
        end: Number.isFinite(end) ? end : undefined,
        timeStr: option.displayLabel,
        appointment: option.hasAppointment ? option.appointment : null,
        modalSegmentTimeStrs: option.segmentSlots,
      };
    }
  );
}

export function buildAppointmentSlotMapForDates(
  allAppointments,
  dateKeys,
  dentistUid,
  slotLabels = STANDARD_OFFICE_SLOT_LABELS
) {
  const map = new Map();
  if (!dentistUid || !Array.isArray(dateKeys)) return map;

  dateKeys.forEach((dateKey) => {
    getBlockingAppointmentsForDentistOnDate(allAppointments, dateKey, dentistUid).forEach(
      (appt) => {
        const range = getAppointmentDisplayRangeMinutes(appt);
        if (!range) return;
        getHalfHourSlotsCoveringRange(range.start, range.end, slotLabels).forEach((slot) => {
          map.set(slot, appt);
        });
      }
    );
  });
  return map;
}

/** Merge contiguous half-hour slots only when they share the same booked appointment. */
export function buildMergedScheduleSlotMenuOptions(
  slotLabels,
  appointmentSlotMap,
  blockedSlotOwnerMap = null
) {
  if (!Array.isArray(slotLabels) || slotLabels.length === 0) return [];

  const getBlockOwner = (slot) => {
    if (!(blockedSlotOwnerMap instanceof Map)) return null;
    return blockedSlotOwnerMap.get(slot) || null;
  };

  const isBlocked = (slot) => getBlockOwner(slot) != null;

  const formatSegmentSlotsLabel = (segmentSlots) => {
    if (!Array.isArray(segmentSlots) || segmentSlots.length === 0) return "";
    if (segmentSlots.length === 1) return segmentSlots[0];
    const firstStart = parseClockLabelToMinutes(
      segmentSlots[0].split(" - ").map((part) => part.trim())[0]
    );
    const lastEnd = parseClockLabelToMinutes(
      segmentSlots[segmentSlots.length - 1]
        .split(" - ")
        .map((part) => part.trim())[1]
    );
    if (Number.isFinite(firstStart) && Number.isFinite(lastEnd)) {
      return formatSlotRangeLabelFromMinutes(firstStart, lastEnd);
    }
    return segmentSlots.join(", ");
  };

  const options = [];
  let index = 0;

  while (index < slotLabels.length) {
    const slot = slotLabels[index];
    const appointment = appointmentSlotMap?.get(slot) || null;
    const appointmentKey = appointmentRecordKeyForSlotMerge(appointment);

    if (appointmentKey) {
      const segmentSlots = [slot];
      let nextIndex = index + 1;
      while (nextIndex < slotLabels.length) {
        const nextSlot = slotLabels[nextIndex];
        const nextAppt = appointmentSlotMap?.get(nextSlot) || null;
        if (appointmentRecordKeyForSlotMerge(nextAppt) !== appointmentKey) break;
        segmentSlots.push(nextSlot);
        nextIndex += 1;
      }

      const range = getAppointmentDisplayRangeMinutes(appointment);
      const displayLabel =
        range && Number.isFinite(range.start) && Number.isFinite(range.end)
          ? formatSlotRangeLabelFromMinutes(range.start, range.end)
          : formatSegmentSlotsLabel(segmentSlots);

      options.push({
        key: `${appointmentKey}:${segmentSlots[0]}`,
        displayLabel,
        segmentSlots,
        hasAppointment: true,
        appointment,
      });
      index = nextIndex;
      continue;
    }

    if (isBlocked(slot)) {
      const ownerKey = getBlockOwner(slot);
      options.push({
        key: `blocked:${ownerKey}:${slot}`,
        displayLabel: slot,
        segmentSlots: [slot],
        hasAppointment: false,
        isBlockedByOtherDentist: true,
        blockedOwnerKey: ownerKey,
      });
      index += 1;
      continue;
    }

    options.push({
      key: slot,
      displayLabel: slot,
      segmentSlots: [slot],
      hasAppointment: false,
    });
    index += 1;
  }

  return options;
}

export function validateSchedulePlotAgainstAppointments({
  dentistDisplayName,
  dentistUid,
  dateKeys,
  selectedSlots,
  allAppointments,
  slotLabels = STANDARD_OFFICE_SLOT_LABELS,
}) {
  if (!dentistUid || !Array.isArray(dateKeys) || dateKeys.length === 0) return;

  const selectedSet = new Set(
    (Array.isArray(selectedSlots) ? selectedSlots : []).filter((slot) =>
      slotLabels.includes(slot)
    )
  );
  const displayName = dentistDisplayName || "This dentist";
  let hasConflict = false;

  dateKeys.forEach((dateKey) => {
    getBlockingAppointmentsForDentistOnDate(allAppointments, dateKey, dentistUid).forEach(
      (appt) => {
        const range = getAppointmentDisplayRangeMinutes(appt);
        if (!range) return;
        const requiredSlots = getHalfHourSlotsCoveringRange(
          range.start,
          range.end,
          slotLabels
        );
        const missingSlots = requiredSlots.filter((slot) => !selectedSet.has(slot));
        if (missingSlots.length > 0) {
          hasConflict = true;
        }
      }
    );
  });

  if (hasConflict) {
    throw new Error(
      `${displayName} has a patient appointment within the assigned timeslots. Please cancel the patient appointment first.`
    );
  }
}

export function isSoftDeletedUserAccount(data) {
  if (!data || typeof data !== "object") return false;
  return (
    data.accountDeleted === true ||
    data.accountDeleted === "true" ||
    data.isDeleted === true ||
    data.deleted === true ||
    (typeof data.accountStatus === "string" &&
      data.accountStatus.toLowerCase() === "deleted") ||
    (typeof data.status === "string" && data.status.toLowerCase() === "deleted")
  );
}

export function loadActiveDentistsFromUserSnapshot(snapshot) {
  const byUid = new Map();

  snapshot.forEach((userDoc) => {
    const data = userDoc.data();
    if (isSoftDeletedUserAccount(data)) return;

    let role = null;
    if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
      role = "DentistOwner";
    } else if (
      data.userType === "AssociateDentist" ||
      data.role === "AssociateDentist"
    ) {
      role = "AssociateDentist";
    }
    if (!role || byUid.has(userDoc.id)) return;

    byUid.set(userDoc.id, {
      uid: userDoc.id,
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: buildDentistDisplayLabelFromUserDoc(data),
      role,
      defaultAvailableFrom: data.defaultAvailableFrom || null,
      defaultAvailableTo: data.defaultAvailableTo || null,
    });
  });

  return [...byUid.values()].sort((a, b) => {
    const roleOrder = { DentistOwner: 0, AssociateDentist: 1 };
    const roleA = roleOrder[a.role] ?? 2;
    const roleB = roleOrder[b.role] ?? 2;
    if (roleA !== roleB) return roleA - roleB;
    const nameA = (a.displayName || `${a.firstName} ${a.lastName}`).toLowerCase();
    const nameB = (b.displayName || `${b.firstName} ${b.lastName}`).toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

export function buildDentistDisplayLabelFromUserDoc(data) {
  if (!data || typeof data !== "object") return "Unknown Dentist";
  const first = (data.firstName || "").trim();
  const last = (data.lastName || "").trim();
  const combined = [first, last].filter(Boolean).join(" ").trim();
  if (combined) {
    return `Dr. ${combined}`;
  }
  const fallback = (data.name || data.displayName || "").trim();
  if (fallback) {
    return /^dr\.?\s/i.test(fallback) ? fallback : `Dr. ${fallback}`;
  }
  return "Unknown Dentist";
}

export function resolveAppointmentDentistUid(appointment = {}) {
  return (
    appointment.dentistId ||
    appointment.dentistUID ||
    appointment.dentistUid ||
    appointment.dentistUserId ||
    appointment.dentistUserID ||
    null
  );
}

export function getLocalDateKey(date = new Date()) {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isBlockingAppointmentStatusForRosterRemoval(status) {
  const s = (status || "").toString().trim().toLowerCase();
  return s === "pending" || s === "confirmed" || s === "new" || s === "reserved";
}

export function computeRosterRemovalAppointmentBlocks(
  removedUids,
  appointmentsSnapshot,
  todayStr,
  resolveAppointmentDentistUidFn
) {
  if (!Array.isArray(removedUids) || removedUids.length === 0) return [];
  const removedSet = new Set(removedUids);
  const byDentist = new Map();
  appointmentsSnapshot.forEach((docSnap) => {
    const dateStr = docSnap.id;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr) || dateStr < todayStr) return;
    const data = docSnap.data();
    if (!data) return;
    Object.values(data).forEach((appt) => {
      if (!appt || typeof appt !== "object") return;
      const uid = resolveAppointmentDentistUidFn(appt);
      if (!uid || !removedSet.has(uid)) return;
      if (!isBlockingAppointmentStatusForRosterRemoval(appt.status)) return;
      if (!byDentist.has(uid)) byDentist.set(uid, new Set());
      byDentist.get(uid).add(dateStr);
    });
  });
  return removedUids
    .filter((uid) => byDentist.has(uid) && byDentist.get(uid).size > 0)
    .map((uid) => ({ uid, dates: [...byDentist.get(uid)].sort() }));
}

export function formatRosterRemovalDateKey(dateKey) {
  const parts = dateKey.split("-").map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dateKey;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function generateTimeSlotsForDate(date) {
  if (!date) return [];

  const timeSlots = [];
  const startHour = 9;
  const endHour = 18;
  const slotDuration = 30;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minutes = 0; minutes < 60; minutes += slotDuration) {
      const startMinutes = hour * 60 + minutes;
      const endMinutes = startMinutes + slotDuration;

      if (endMinutes > endHour * 60) {
        continue;
      }

      const startDisplayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const startAmpm = hour < 12 ? "AM" : "PM";
      const startTimeString = `${startDisplayHour}:${minutes.toString().padStart(2, "0")} ${startAmpm}`;

      const endHour24 = Math.floor(endMinutes / 60);
      const endMinutes24 = endMinutes % 60;
      const endDisplayHour =
        endHour24 === 0 ? 12 : endHour24 > 12 ? endHour24 - 12 : endHour24;
      const endAmpm = endHour24 < 12 ? "AM" : "PM";
      const endTimeString = `${endDisplayHour}:${endMinutes24.toString().padStart(2, "0")} ${endAmpm}`;

      timeSlots.push(`${startTimeString} - ${endTimeString}`);
    }
  }

  return timeSlots;
}

export function flattenAppointmentsSnapshot(snapshot) {
  const rows = [];
  snapshot.forEach((docSnap) => {
    const dateStr = docSnap.id;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
    const data = docSnap.data();
    if (!data) return;
    Object.entries(data).forEach(([id, value]) => {
      if (!value || typeof value !== "object") return;
      rows.push({
        ...value,
        id,
        date: value.date || dateStr,
      });
    });
  });
  return rows;
}

export function getAppointmentCountsForDate(date, allAppointments) {
  if (!date) return { pending: 0, confirmed: 0, completed: 0 };

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateStr = `${year}-${month}-${day}`;

  const dateAppointments = (allAppointments || []).filter(
    (appointment) => appointment.date === dateStr
  );

  const counts = { pending: 0, confirmed: 0, completed: 0 };
  dateAppointments.forEach((appointment) => {
    const status = (appointment.status || "").toString().trim().toLowerCase();
    if (status === "pending" || status === "new") {
      counts.pending++;
    } else if (status === "confirmed") {
      counts.confirmed++;
    } else if (status === "completed") {
      counts.completed++;
    }
  });

  return counts;
}

export { isBlockingAppointmentStatus };
